import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, StatCard, SeverityBadge, Spinner } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { timeAgo } from "@/lib/ui";
import { onLive } from "@/lib/live";
import { toast } from "sonner";
import {
  ServerCrash, MonitorSmartphone, BellRing, Ticket, ShieldAlert, RefreshCw,
  HardDrive, Bot, ServerOff, AlertTriangle, Timer, ShieldCheck,
} from "lucide-react";

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [posture, setPosture] = useState(null);
  const [reboots, setReboots] = useState(null);
  const nav = useNavigate();
  const loadReboots = () => api.get("/patches/reboots-pending").then((r) => setReboots(r.data)).catch(() => {});
  const load = () => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); loadReboots(); };
  const rebootNow = async (id) => { try { await api.post(`/devices/${id}/reboot`, { when: null, reason: "Complete pending updates" }); toast.success("Reboot command sent"); loadReboots(); } catch { toast.error("Could not reboot"); } };
  useEffect(() => { load(); api.get("/admin/security-posture").then((r) => setPosture(r.data)).catch(() => {}); const t = setInterval(load, 20000); const off = onLive(() => load()); return () => { clearInterval(t); off(); }; }, []);

  if (!d) return <div className="p-6"><Spinner /></div>;
  const dev = d.devices;

  return (
    <div className="p-6 fade-up">
      <PageHeader title="Operations Dashboard" subtitle="What's broken, what needs attention, what changed"
        testid="dashboard-page" />

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-4">
        <StatCard label="Devices" value={dev.total} sub={`${dev.online} online · ${dev.offline} offline`} icon={MonitorSmartphone} tone="cyan" onClick={() => nav("/devices")} testid="stat-devices" />
        <StatCard label="Critical Alerts" value={d.alerts.critical} sub={`${d.alerts.open} open total`} icon={ShieldAlert} tone="rose" onClick={() => nav("/alerts")} testid="stat-critical" />
        <StatCard label="Offline Servers" value={dev.offline_servers} icon={ServerOff} tone="rose" onClick={() => nav("/devices?status=OFFLINE")} />
        <StatCard label="Offline Workstations" value={dev.offline_workstations} icon={ServerCrash} tone="amber" />
        <StatCard label="Pending Reboots" value={d.pending_reboots} icon={RefreshCw} tone="amber" />
        <StatCard label="Disk Warnings" value={d.disk_warnings} icon={HardDrive} tone="amber" />
        <StatCard label="Open Tickets" value={d.tickets.open} sub={`${d.tickets.urgent} urgent`} icon={Ticket} tone="sky" onClick={() => nav("/tickets")} />
        <StatCard label="Failed Automations" value={d.failed_jobs} icon={AlertTriangle} tone="rose" />
        <StatCard label="SLA Response Met" value={(d.sla?.response_pct ?? 100) + "%"} sub={`${d.sla?.response_total || 0} tickets`} icon={Timer} tone={(d.sla?.response_pct ?? 100) >= 90 ? "emerald" : "amber"} onClick={() => nav("/reports")} testid="stat-sla" />
        <StatCard label="Awaiting Response" value={d.response_overdue_total || 0} sub="past SLA" icon={Timer} tone={(d.response_overdue_total || 0) > 0 ? "rose" : "emerald"} onClick={() => nav("/tickets")} testid="stat-awaiting-response" />
      </div>

      {posture && (
        <div className="card-surface rounded-lg p-4 mb-4 cursor-pointer border border-transparent hover:border-cyan-800 transition-colors" data-testid="security-posture-card" onClick={() => nav("/admin")}>
          <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} className="text-cyan-400" /><h2 className="font-heading font-bold text-[14px] text-slate-200">Security Posture</h2><span className="ml-auto text-[11px] text-slate-500">Manage in Users &amp; Security →</span></div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Posture label="MFA coverage" value={posture.mfa_pct + "%"} sub={`${posture.mfa_enabled}/${posture.users} users`} good={posture.mfa_pct >= 80} warn={posture.mfa_pct < 50} />
            <Posture label="Enforce MFA" value={posture.enforce_mfa ? "On" : "Off"} good={posture.enforce_mfa} warn={!posture.enforce_mfa} />
            <Posture label="Locked accounts" value={posture.locked_accounts} good={posture.locked_accounts === 0} warn={posture.locked_accounts > 0} />
            <Posture label="Disabled accounts" value={posture.disabled_accounts} good />
            <Posture label="Active sessions" value={posture.active_sessions} good />
            <Posture label="Active webhooks" value={posture.active_webhooks} good />
          </div>
          <div className="text-[11px] text-slate-600 mt-2">Last audit event {posture.last_audit_at ? timeAgo(posture.last_audit_at) : "—"}</div>
        </div>
      )}

      {(d.response_overdue || []).length > 0 && (
        <div className="card-surface rounded-lg overflow-hidden mb-4 border border-rose-900/50" data-testid="sla-nudge-card">
          <div className="px-4 py-2.5 border-b border-slate-800 flex items-center gap-2 bg-rose-950/20">
            <Timer size={15} className="text-rose-400" />
            <h2 className="font-heading font-bold text-[14px] text-slate-200">Tickets awaiting a technician response — past SLA</h2>
            <span className="ml-auto text-[11px] text-rose-300 font-bold">{d.response_overdue_total}</span>
          </div>
          <div className="divide-y divide-slate-800/70">
            {d.response_overdue.map((t) => (
              <div key={t.id} className="px-4 py-2.5 flex items-center gap-3 hover-row cursor-pointer" onClick={() => nav("/tickets")} data-testid="sla-nudge-row">
                <span className="text-[10px] font-bold uppercase rounded px-1.5 py-0.5 bg-rose-950/60 text-rose-300">{t.priority}</span>
                <div className="flex-1 min-w-0"><div className="text-[13px] text-slate-200 truncate">#{t.number} · {t.subject}</div><div className="text-[11px] text-slate-500">{t.customer_name}</div></div>
                <span className="text-[11px] text-rose-300">due {timeAgo(t.due)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {reboots && (reboots.overdue_count || 0) > 0 && (
        <div className="card-surface rounded-lg overflow-hidden mb-4 border border-amber-900/50" data-testid="reboot-nudge-card">
          <div className="px-4 py-2.5 border-b border-slate-800 flex items-center gap-2 bg-amber-950/20">
            <RefreshCw size={15} className="text-amber-400" />
            <h2 className="font-heading font-bold text-[14px] text-slate-200">Endpoints awaiting a reboot — over {reboots.threshold_hours}h</h2>
            <span className="ml-auto text-[11px] text-amber-300 font-bold">{reboots.overdue_count}</span>
          </div>
          <div className="divide-y divide-slate-800/70">
            {reboots.devices.filter((r) => r.overdue).slice(0, 8).map((r) => (
              <div key={r.device_id} className="px-4 py-2.5 flex items-center gap-3 hover-row" data-testid="reboot-nudge-row">
                <span className="text-[10px] font-bold uppercase rounded px-1.5 py-0.5 bg-amber-950/60 text-amber-300">{r.hours_pending != null ? `${Math.round(r.hours_pending)}h` : "pending"}</span>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => nav(`/devices/${r.device_id}`)}><div className="text-[13px] text-slate-200 truncate font-mono">{r.hostname}</div><div className="text-[11px] text-slate-500">{r.customer_name}</div></div>
                <button data-testid={`reboot-now-${r.device_id}`} onClick={() => rebootNow(r.device_id)} className="text-[11px] text-amber-300 bg-amber-950/40 border border-amber-900 rounded px-2 py-0.5 inline-flex items-center gap-1"><RefreshCw size={11} /> Reboot now</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-surface rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-800 flex items-center gap-2">
            <BellRing size={15} className="text-rose-400" />
            <h2 className="font-heading font-bold text-[14px] text-slate-200">Active Alerts</h2>
          </div>
          {d.recent_alerts.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-600">No active alerts — all clear.</div>
          ) : (
            <div className="divide-y divide-slate-800/70">
              {d.recent_alerts.map((a) => (
                <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 hover-row cursor-pointer" onClick={() => nav("/alerts")}>
                  <SeverityBadge severity={a.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-slate-200 truncate">{a.title}</div>
                    <div className="text-[11px] text-slate-500">{a.customer_name} · {timeAgo(a.first_detected)}</div>
                  </div>
                  <span className="text-[11px] font-mono text-slate-400">{a.current_value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-surface rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-800 flex items-center gap-2">
            <ServerOff size={15} className="text-slate-400" />
            <h2 className="font-heading font-bold text-[14px] text-slate-200">Offline Devices</h2>
          </div>
          {d.offline_list.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-600">Every device is reporting in.</div>
          ) : (
            <div className="divide-y divide-slate-800/70">
              {d.offline_list.map((o) => (
                <div key={o.id} className="px-4 py-2.5 flex items-center gap-3 hover-row cursor-pointer" onClick={() => nav("/devices/" + o.id)}>
                  <StatusBadge status={o.status} small />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-slate-200 truncate font-mono">{o.hostname}</div>
                    <div className="text-[11px] text-slate-500">{o.customer_name} · {o.os}</div>
                  </div>
                  <span className="text-[11px] text-slate-500">{timeAgo(o.last_seen)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Posture({ label, value, sub, good, warn }) {
  const color = warn ? "text-amber-300" : good ? "text-emerald-300" : "text-slate-200";
  return (
    <div className="bg-slate-900/50 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={"font-heading font-bold text-lg " + color}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}
